import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function Stars({
  count
}: {
  count: number;
}) {
  return <div className="flex items-center gap-0.5" data-id="element-388">
      {Array.from({
      length: count
    }).map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 text-gold fill-gold" data-id="element-389" />)}
    </div>;
}
export function TestimonialsSection() {
  const {
    t
  } = useLanguage();
  const testimonials = [{
    nameKey: 'testimonials.1.name' as const,
    locationKey: 'testimonials.1.location' as const,
    textKey: 'testimonials.1.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=47',
    stars: 5
  }, {
    nameKey: 'testimonials.2.name' as const,
    locationKey: 'testimonials.2.location' as const,
    textKey: 'testimonials.2.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=12',
    stars: 5
  }, {
    nameKey: 'testimonials.3.name' as const,
    locationKey: 'testimonials.3.location' as const,
    textKey: 'testimonials.3.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=32',
    stars: 5
  }, {
    nameKey: 'testimonials.4.name' as const,
    locationKey: 'testimonials.4.location' as const,
    textKey: 'testimonials.4.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=15',
    stars: 5
  }, {
    nameKey: 'testimonials.5.name' as const,
    locationKey: 'testimonials.5.location' as const,
    textKey: 'testimonials.5.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=44',
    stars: 5
  }, {
    nameKey: 'testimonials.6.name' as const,
    locationKey: 'testimonials.6.location' as const,
    textKey: 'testimonials.6.text' as const,
    avatar: 'https://i.pravatar.cc/150?img=8',
    stars: 5
  }];
  return <section className="relative w-full py-8 sm:py-14 overflow-hidden" data-id="element-390">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gold/[0.03] rounded-full blur-[150px] pointer-events-none" data-id="element-391" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-392">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-6 sm:mb-10" data-id="element-393">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-394">
            {t('testimonials.eyebrow')}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-395">
            {t('testimonials.title')}
          </h2>
          {/* Aggregate rating */}
          <div className="flex items-center justify-center gap-2 mt-4" data-id="element-396">
            <div className="flex items-center gap-0.5" data-id="element-397">
              {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className="w-5 h-5 text-gold fill-gold" data-id="element-398" />)}
            </div>
            <span className="font-serif text-white font-bold text-lg" data-id="element-399">5.0</span>
            <span className="font-sans text-white/40 text-sm" data-id="element-400">
              {t('testimonials.readers')}
            </span>
          </div>
        </motion.div>

        {/* Testimonials grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-401">
          {testimonials.map((testimonial, index) => <motion.div key={index} variants={cardVariants} className="relative" data-id="element-402">
              <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-6 h-full overflow-hidden" data-id="element-403">
                {/* Top accent line */}
                <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" data-id="element-404" />

                {/* Stars */}
                <Stars count={testimonial.stars} data-id="element-405" />

                {/* Quote */}
                <p className="font-sans text-white/60 text-sm leading-relaxed mt-3 mb-5" data-id="element-406">
                  "{t(testimonial.textKey)}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 mt-auto" data-id="element-407">
                  <img src={testimonial.avatar} alt={t(testimonial.nameKey)} className="w-10 h-10 rounded-full object-cover border border-white/10" loading="lazy" data-id="element-408" />
                  <div data-id="element-409">
                    <p className="font-sans text-white text-sm font-medium" data-id="element-410">
                      {t(testimonial.nameKey)}
                    </p>
                    <p className="font-sans text-white/30 text-xs" data-id="element-411">
                      {t(testimonial.locationKey)}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom trust line */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.4
      }} className="font-sans text-white/25 text-xs text-center mt-5 sm:mt-8" data-id="element-412">
          {t('testimonials.verified')}
        </motion.p>
      </div>
    </section>;
}